import { useEffect, useRef } from 'react';

/**
 * Custom hook to manage nuclear-related effects (enrichment warnings, status reports)
 * @param {Object} game - Game state object
 * @param {Function} setEnrichmentWarning - Setter for enrichment warning
 * @param {Function} setShowNuclearStatusReport - Setter for nuclear status report
 * @param {Boolean} isProcessingAiTurns - Whether AI turns are currently being processed
 * @returns {Object} - Refs for tracking warnings
 */
export function useNuclearEffects(game, setEnrichmentWarning, setShowNuclearStatusReport, isProcessingAiTurns = false) {
  const enrichmentWarningsTriggered = useRef({});
  const nuclearReportShownTurns = useRef(new Set());
  const isInitialLoad = useRef(true); // Track if this is the first render

  // Monitor enrichment levels and trigger warnings at 75% and 90%
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Note: enrichmentWarningsTriggered is a ref - intentionally not in deps
  useEffect(() => {
    if (!game || !game.countries) return;
    
    // Skip warnings during AI turn processing to avoid blocking the animations
    if (isProcessingAiTurns) return;
    
    // Skip warnings on initial load to avoid showing stale warnings from saved games
    // Only start showing warnings after the first game state update
    if (isInitialLoad.current) {
      // Mark all current enrichment levels as "already warned" so we don't show old warnings
      game.countries.forEach(country => {
        const enrichment = country.resources?.uranium_enrichment || 0;
        const countryCode = country.code;
        
        if (!enrichmentWarningsTriggered.current[countryCode]) {
          enrichmentWarningsTriggered.current[countryCode] = { warning75: false, warning90: false };
        }
        
        // Mark as warned if already above thresholds
        if (enrichment >= 90) {
          enrichmentWarningsTriggered.current[countryCode].warning90 = true;
        } else if (enrichment >= 75) {
          enrichmentWarningsTriggered.current[countryCode].warning75 = true;
        }
      });
      
      isInitialLoad.current = false;
      return;
    }
    
    game.countries.forEach(country => {
      const enrichment = country.resources?.uranium_enrichment || 0;
      const countryCode = country.code;
      
      if (!enrichmentWarningsTriggered.current[countryCode]) {
        enrichmentWarningsTriggered.current[countryCode] = { warning75: false, warning90: false };
      }
      
      // Check for 90% warning (more urgent)
      if (enrichment >= 90 && !enrichmentWarningsTriggered.current[countryCode].warning90) {
        console.log(`🚨 CRITICAL: ${country.name} reached ${enrichment}% enrichment!`);
        enrichmentWarningsTriggered.current[countryCode].warning90 = true;
        setEnrichmentWarning({ country, enrichmentLevel: enrichment });
      }
      // Check for 75% warning
      else if (enrichment >= 75 && enrichment < 90 && !enrichmentWarningsTriggered.current[countryCode].warning75) {
        console.log(`⚠️ WARNING: ${country.name} reached ${enrichment}% enrichment!`);
        enrichmentWarningsTriggered.current[countryCode].warning75 = true;
        setEnrichmentWarning({ country, enrichmentLevel: enrichment });
      }
    });
  }, [game, setEnrichmentWarning, isProcessingAiTurns]);

  // Check for Nuclear Status Report every 10 turns
  useEffect(() => {
    if (!game || !game.current_turn) return;
    
    // ⭐ Skip on initial load to avoid showing report for loaded games
    if (isInitialLoad.current) {
      const currentTurn = game.current_turn;
      // Mark this turn as already shown to prevent future triggers
      if (currentTurn % 10 === 0 && currentTurn > 0) {
        nuclearReportShownTurns.current.add(currentTurn);
      }
      return;
    }
    
    const currentTurn = game.current_turn;
    
    if (currentTurn % 10 === 0 && currentTurn > 0 && !nuclearReportShownTurns.current.has(currentTurn)) {
      console.log(`☢️ Turn ${currentTurn}: Showing mandatory Nuclear Status Report`);
      nuclearReportShownTurns.current.add(currentTurn);
      setShowNuclearStatusReport(true);
    }
  }, [game?.current_turn, setShowNuclearStatusReport]);

  return { enrichmentWarningsTriggered, nuclearReportShownTurns };
}
